import { useMemo, useState } from "react";
import type { ReactNode } from "react";
import { ChevronRight, GitCommitHorizontal } from "lucide-react";
import { getDeployments } from "../api/client.js";
import type {
  CommitDeployState,
  CommitItem,
  DeploymentsResponse,
  EnvDeployment,
  WorkflowRunItem,
  WorkflowRunStatus,
} from "../api/types.js";
import { useApi } from "../lib/useApi.js";
import { formatTime } from "../lib/format.js";
import { Card, Chip, EmptyState, Pill, SectionTitle, StatusDot } from "../components/ui.js";
import type { Tone } from "../components/ui.js";
import { ErrorState, LoadingState, SectionErrors } from "../components/common.js";

export interface DeploymentsPageProps {
  refreshKey: number;
}

const RUNS_GRID = "88px minmax(0, 1.12fr) minmax(0, 1.45fr) 66px 96px 16px";
const RUNS_MIN_WIDTH = 760;

const RUN_TONE: Record<WorkflowRunStatus, Tone> = {
  success: "ok",
  failed: "alarm",
  running: "info",
  queued: "nodata",
  cancelled: "nodata",
  skipped: "nodata",
};

const COMMIT_STATE: Record<CommitDeployState, { label: string; tone: Tone; pulse: boolean }> = {
  deploying: { label: "deploying", tone: "info", pulse: true },
  prod: { label: "in prod", tone: "ok", pulse: false },
  staging: { label: "in staging", tone: "nodata", pulse: false },
  undeployed: { label: "not deployed", tone: "nodata", pulse: false },
};

const ENV_COLOR: Record<EnvDeployment["env"], string> = {
  prod: "var(--acct-prod)",
  staging: "var(--acct-staging)",
};

const PAGE_STYLES = `
@keyframes ops-deploy-pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.35; } }
.deploy-run-row:hover { background: var(--bg-subtle); }
`;

type StatusFilter = "all" | "failed" | "running" | "success";

// Server contract for github.packages[]; typed locally (and fields optional) until
// the shared types mirror catches up — the section renders only when data is present.
interface PackageCi {
  repo: string;
  label: string;
  workflow?: string | null;
  status?: string | null;
  branch?: string | null;
  sha?: string | null;
  when?: string | null;
  url?: string | null;
}

function isPackageCi(value: unknown): value is PackageCi {
  if (!value || typeof value !== "object") return false;
  const v = value as Record<string, unknown>;
  return typeof v.repo === "string" && typeof v.label === "string";
}

function extractPackages(data: DeploymentsResponse): PackageCi[] {
  const loose = data as DeploymentsResponse & {
    packages?: unknown;
    github: DeploymentsResponse["github"] & { packages?: unknown };
  };
  const raw = loose.github?.packages ?? loose.packages;
  if (!Array.isArray(raw)) return [];
  return raw.filter(isPackageCi);
}

function packageStatusView(status: string | null | undefined): { label: string; tone: Tone; pulse: boolean } {
  switch (status) {
    case "success":
      return { label: "Success", tone: "ok", pulse: false };
    case "failed":
      return { label: "Failed", tone: "alarm", pulse: false };
    case "running":
      return { label: "Running", tone: "info", pulse: true };
    case "queued":
    case "cancelled":
    case "skipped":
      return { label: status.charAt(0).toUpperCase() + status.slice(1), tone: "nodata", pulse: false };
    default:
      return { label: "No CI", tone: "nodata", pulse: false };
  }
}

function relativeTime(iso: string | null | undefined): string {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "";
  const mins = Math.floor((Date.now() - t) / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return formatDay(iso);
}

function shortSha(sha: string | null): string {
  return sha ? sha.slice(0, 7) : "";
}

function formatDay(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("en-US", { month: "short", day: "numeric" });
}

function formatDuration(seconds: number | null): string {
  if (seconds === null) return "--";
  const mins = Math.floor(seconds / 60);
  if (mins >= 60) {
    return `${Math.floor(mins / 60)}h ${String(mins % 60).padStart(2, "0")}m`;
  }
  return `${mins}m ${String(Math.round(seconds % 60)).padStart(2, "0")}s`;
}

function PulsingDot({ pulse }: { pulse: boolean }) {
  return (
    <span
      aria-hidden="true"
      style={{
        width: 6,
        height: 6,
        borderRadius: 999,
        background: "currentColor",
        animation: pulse ? "ops-deploy-pulse 1.6s ease-in-out infinite" : undefined,
      }}
    />
  );
}

interface EnvView {
  pill: string;
  tone: Tone;
  pulse: boolean;
  version: string;
  line1: string;
  foot: string;
}

function envView(env: EnvDeployment, driftAheadOfProd: number | null): EnvView {
  const { rollout } = env;
  let pill = "Unknown";
  let tone: Tone = "nodata";
  if (rollout.state === "deploying") {
    pill = "Deploying";
    tone = "info";
  } else if (rollout.state === "failed") {
    pill = "Rollout failed";
    tone = "alarm";
  } else if (!env.healthy) {
    pill = "Unreachable";
    tone = "alarm";
  } else if (rollout.state === "steady") {
    pill = "Stable";
    tone = "ok";
  }

  const version = env.commit ? `${env.branch ?? "?"} @ ${shortSha(env.commit)}` : "--";
  const line1 = env.healthy
    ? `Deployed ${formatTime(env.buildTime) || "--"} · ${env.apiHost}`
    : `Health endpoint unreachable · ${env.apiHost}`;

  const tasks =
    rollout.runningCount !== null && rollout.desiredCount !== null
      ? `${rollout.runningCount}/${rollout.desiredCount} tasks running`
      : "ECS state unavailable";
  const tail =
    env.env === "prod" && driftAheadOfProd !== null
      ? driftAheadOfProd === 0
        ? "in sync with staging"
        : `${driftAheadOfProd} commits behind staging`
      : (rollout.taskDefinition ?? env.apiHost);

  return { pill, tone, pulse: rollout.state === "deploying", version, line1, foot: `${tasks} · ${tail}` };
}

function PackageCard({ pkg }: { pkg: PackageCi }) {
  const status = packageStatusView(pkg.status);
  const ref = [pkg.branch, shortSha(pkg.sha ?? null)].filter(Boolean).join(" @ ");
  const when = relativeTime(pkg.when);
  const inner = (
    <>
      <span style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8 }}>
        <span style={{ minWidth: 0, display: "flex", flexDirection: "column", gap: 2 }}>
          <span
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: "var(--fg)",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {pkg.label}
          </span>
          <span
            className="mono"
            style={{
              fontSize: 10.5,
              color: "var(--fg-faint)",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {pkg.repo}
          </span>
        </span>
        <span style={{ flex: "none" }}>
          <Pill tone={status.tone}>
            <PulsingDot pulse={status.pulse} />
            {status.label}
          </Pill>
        </span>
      </span>
      <span
        style={{
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
          gap: 8,
          borderTop: "1px solid var(--divider)",
          paddingTop: 8,
        }}
      >
        <span
          className="mono"
          style={{
            fontSize: 11,
            color: "var(--accent)",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {ref || "--"}
        </span>
        <span style={{ flex: "none", fontSize: 11, color: "var(--fg-subtle)" }}>{when}</span>
      </span>
    </>
  );

  const style = { display: "flex", flexDirection: "column", gap: 9 } as const;
  if (pkg.url) {
    return (
      <a
        className="card card--tight card--interactive"
        href={pkg.url}
        target="_blank"
        rel="noreferrer"
        title={pkg.workflow ?? undefined}
        style={{ ...style, textDecoration: "none" }}
      >
        {inner}
      </a>
    );
  }
  return (
    <Card tight style={style}>
      {inner}
    </Card>
  );
}

function PackagesSection({ data }: { data: DeploymentsResponse }) {
  const packages = extractPackages(data);
  if (packages.length === 0) return null;
  const failing = packages.filter((p) => p.status === "failed").length;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <SectionTitle
        title="Packages"
        subtitle="Latest CI run per repo · GitHub Actions"
        actions={
          failing > 0 ? (
            <span style={{ fontSize: 11.5, fontWeight: 600, color: "var(--tone-alarm-fg)" }}>
              {failing} failing
            </span>
          ) : undefined
        }
      />
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
          gap: 12,
        }}
      >
        {packages.map((pkg) => (
          <PackageCard key={`${pkg.repo}-${pkg.label}`} pkg={pkg} />
        ))}
      </div>
    </div>
  );
}

function SummaryStat({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <span style={{ display: "flex", flexDirection: "column", gap: 1 }}>
      <span className="label--table">{label}</span>
      <span className="mono" style={{ fontSize: 14, fontWeight: 500, color: color ?? "var(--fg)" }}>
        {value}
      </span>
    </span>
  );
}

function SummaryStrip({ data }: { data: DeploymentsResponse }) {
  const prod = data.environments.find((e) => e.env === "prod");
  const staging = data.environments.find((e) => e.env === "staging");
  const { configured, runsLast24h } = data.github;

  const envValue = (env: EnvDeployment | undefined): { v: string; color?: string } => {
    if (!env) return { v: "--" };
    if (env.rollout.state === "deploying") {
      return {
        v: env.commit ? `deploying ${shortSha(env.commit)}` : "deploying",
        color: "var(--tone-info-fg)",
      };
    }
    if (!env.commit) return { v: "--" };
    const day = formatDay(env.buildTime);
    return { v: day ? `${shortSha(env.commit)} · ${day}` : shortSha(env.commit) };
  };

  const prodStat = envValue(prod);
  const stagingStat = envValue(staging);
  const drift =
    data.driftAheadOfProd === null
      ? "--"
      : data.driftAheadOfProd === 0
        ? "in sync"
        : `staging +${data.driftAheadOfProd} commits`;
  const actions = configured
    ? `${runsLast24h.total} runs · ${runsLast24h.failed ? `${runsLast24h.failed} failed` : "all green"}`
    : "--";
  const deployingCount = data.environments.filter((e) => e.rollout.state === "deploying").length;
  const steadyCount = data.environments.filter((e) => e.rollout.state === "steady").length;
  const services = deployingCount
    ? `${deployingCount} deploying · ${steadyCount} steady`
    : steadyCount === data.environments.length && steadyCount > 0
      ? "all steady"
      : `${steadyCount} steady`;

  return (
    <Card
      style={{
        display: "flex",
        alignItems: "center",
        gap: 26,
        flexWrap: "wrap",
        padding: "13px 20px",
      }}
    >
      <SummaryStat label="Prod" value={prodStat.v} color={prodStat.color} />
      <SummaryStat label="Staging" value={stagingStat.v} color={stagingStat.color} />
      <SummaryStat label="Drift" value={drift} />
      <SummaryStat
        label="Actions · 24 h"
        value={actions}
        color={
          !configured
            ? "var(--fg-subtle)"
            : runsLast24h.failed
              ? "var(--tone-alarm-fg)"
              : "var(--tone-ok-fg)"
        }
      />
      <SummaryStat
        label="ECS services"
        value={services}
        color={deployingCount ? "var(--tone-info-fg)" : steadyCount ? "var(--tone-ok-fg)" : undefined}
      />
      <span style={{ marginLeft: "auto", fontSize: 11.5, color: "var(--fg-faint)" }}>
        github.com/{data.github.org} · GitOps · read-only
      </span>
    </Card>
  );
}

function EnvCard({ env, driftAheadOfProd }: { env: EnvDeployment; driftAheadOfProd: number | null }) {
  const view = envView(env, driftAheadOfProd);
  return (
    <Card tight style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <StatusDot square size={9} color={ENV_COLOR[env.env]} />
        <span className="display" style={{ fontSize: 13.5, flex: 1 }}>
          {env.env}
        </span>
        <Pill tone={view.tone}>
          <PulsingDot pulse={view.pulse} />
          {view.pill}
        </Pill>
      </span>
      <span className="mono" style={{ fontSize: 16.5, fontWeight: 500, letterSpacing: "-0.01em", color: "var(--fg)" }}>
        {view.version}
      </span>
      <span style={{ fontSize: 11.5, lineHeight: 1.5, color: "var(--fg-subtle)" }}>{view.line1}</span>
      <span
        style={{
          fontSize: 11.5,
          color: "var(--fg-subtle)",
          borderTop: "1px solid var(--divider)",
          paddingTop: 9,
        }}
      >
        {view.foot}
      </span>
    </Card>
  );
}

function HeaderRight({ children }: { children: ReactNode }) {
  return <span style={{ fontSize: 11.5, color: "var(--fg-faint)" }}>{children}</span>;
}

function RunRow({ run }: { run: WorkflowRunItem }) {
  const tone = RUN_TONE[run.status];
  const trigger = [run.repo, run.event, run.branch].filter(Boolean).join(" · ");
  return (
    <a
      className="deploy-run-row"
      href={run.url || undefined}
      target="_blank"
      rel="noreferrer"
      style={{
        display: "grid",
        gridTemplateColumns: RUNS_GRID,
        gap: "0 12px",
        alignItems: "center",
        padding: "10px 20px",
        borderBottom: "1px solid var(--divider)",
        textDecoration: "none",
      }}
    >
      <span>
        <Pill tone={tone}>
          <PulsingDot pulse={run.status === "running"} />
          {run.status.toUpperCase()}
        </Pill>
      </span>
      <span style={{ minWidth: 0, display: "flex", flexDirection: "column", gap: 1 }}>
        <span
          style={{
            fontSize: 12.5,
            fontWeight: 600,
            color: "var(--fg)",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {run.workflow}
        </span>
        <span
          style={{
            fontSize: 11,
            color: "var(--fg-subtle)",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {trigger}
        </span>
      </span>
      <span style={{ minWidth: 0, display: "flex", alignItems: "center", gap: 7 }}>
        <span className="mono" style={{ fontSize: 11, color: "var(--accent)", flex: "none" }}>
          {run.sha || "--"}
        </span>
        <span
          style={{
            fontSize: 11.5,
            color: "var(--fg-muted)",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {run.message}
        </span>
      </span>
      <span className="mono" style={{ fontSize: 11, color: "var(--fg-subtle)", textAlign: "right" }}>
        {formatDuration(run.durationSeconds)}
      </span>
      <span style={{ fontSize: 11, color: "var(--fg-subtle)", textAlign: "right" }}>
        {formatTime(run.startedAt)}
      </span>
      <ChevronRight size={14} aria-hidden="true" style={{ color: "var(--fg-faint)", flex: "none" }} />
    </a>
  );
}

function WorkflowRunsCard({ data }: { data: DeploymentsResponse }) {
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [repoFilter, setRepoFilter] = useState<string>("all");
  const { runs, repos, configured } = data.github;

  const counts = useMemo(
    () => ({
      all: runs.length,
      failed: runs.filter((r) => r.status === "failed").length,
      running: runs.filter((r) => r.status === "running").length,
      success: runs.filter((r) => r.status === "success").length,
    }),
    [runs],
  );

  const filtered = runs.filter(
    (r) =>
      (statusFilter === "all" || r.status === statusFilter) &&
      (repoFilter === "all" || r.repo === repoFilter),
  );

  return (
    <Card flush>
      <div style={{ display: "flex", flexDirection: "column", gap: 11, padding: "16px 20px 12px" }}>
        <div
          style={{
            display: "flex",
            alignItems: "baseline",
            justifyContent: "space-between",
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          <span className="display" style={{ fontSize: 14.5 }}>
            Workflow runs
          </span>
          <HeaderRight>GitHub Actions · last {runs.length || 10}</HeaderRight>
        </div>
        {configured && (
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <Chip active={statusFilter === "all"} onClick={() => setStatusFilter("all")} count={counts.all}>
              All
            </Chip>
            <Chip
              active={statusFilter === "failed"}
              onClick={() => setStatusFilter("failed")}
              dot="var(--tone-alarm-dot)"
              count={counts.failed}
            >
              Failed
            </Chip>
            <Chip
              active={statusFilter === "running"}
              onClick={() => setStatusFilter("running")}
              dot="var(--tone-info-dot)"
              count={counts.running}
            >
              Running
            </Chip>
            <Chip
              active={statusFilter === "success"}
              onClick={() => setStatusFilter("success")}
              dot="var(--tone-ok-dot)"
              count={counts.success}
            >
              Success
            </Chip>
            <span className="chip-divider" />
            <Chip active={repoFilter === "all"} onClick={() => setRepoFilter("all")}>
              All repos
            </Chip>
            {repos.map((repo) => (
              <Chip key={repo} active={repoFilter === repo} onClick={() => setRepoFilter(repo)}>
                {repo}
              </Chip>
            ))}
          </div>
        )}
      </div>
      <div style={{ borderTop: "1px solid var(--divider)" }}>
        {!configured ? (
          <EmptyState
            title="GitHub not configured"
            message="Set GITHUB_TOKEN on the server to load workflow runs, commits and drift."
            tone="nodata"
          />
        ) : filtered.length === 0 ? (
          <div style={{ padding: 22, textAlign: "center", fontSize: 12, color: "var(--fg-subtle)" }}>
            No runs match the current filters.
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <div style={{ minWidth: RUNS_MIN_WIDTH, display: "flex", flexDirection: "column" }}>
              {filtered.map((run) => (
                <RunRow key={`${run.repo}-${run.id}`} run={run} />
              ))}
            </div>
          </div>
        )}
      </div>
    </Card>
  );
}

function CommitRow({ commit }: { commit: CommitItem }) {
  const state = COMMIT_STATE[commit.deployState];
  return (
    <div
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 10,
        padding: "9px 0",
        borderBottom: "1px solid var(--divider)",
      }}
    >
      <span className="mono" style={{ fontSize: 11, color: "var(--accent)", flex: "none", paddingTop: 1 }}>
        {shortSha(commit.sha)}
      </span>
      <span style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 1 }}>
        <span
          style={{
            fontSize: 12,
            fontWeight: 600,
            color: "var(--fg)",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {commit.message}
        </span>
        <span style={{ fontSize: 11, color: "var(--fg-subtle)" }}>
          {commit.author} · {formatTime(commit.date)}
        </span>
      </span>
      <span style={{ flex: "none" }}>
        <Pill tone={state.tone}>
          <PulsingDot pulse={state.pulse} />
          {state.label}
        </Pill>
      </span>
    </div>
  );
}

function CommitsCard({ data }: { data: DeploymentsResponse }) {
  const { commits, commitsSource, configured } = data.github;
  return (
    <Card style={{ padding: "16px 20px", display: "flex", flexDirection: "column", gap: 10 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <GitCommitHorizontal size={15} aria-hidden="true" style={{ color: "var(--fg-subtle)", flex: "none" }} />
        <span className="display" style={{ fontSize: 14.5, flex: 1 }}>
          Latest commits
        </span>
        {commitsSource && (
          <span className="mono" style={{ fontSize: 10.5, color: "var(--fg-faint)" }}>
            {commitsSource.repo} · {commitsSource.branch}
          </span>
        )}
      </div>
      {!configured || commits.length === 0 ? (
        <EmptyState
          title={configured ? "No commits" : "GitHub not configured"}
          message={
            configured
              ? "The commits feed returned nothing."
              : "Set GITHUB_TOKEN on the server to load the commit feed."
          }
          tone="nodata"
        />
      ) : (
        <div style={{ display: "flex", flexDirection: "column" }}>
          {commits.map((commit) => (
            <CommitRow key={commit.sha} commit={commit} />
          ))}
        </div>
      )}
    </Card>
  );
}

export function DeploymentsPage({ refreshKey }: DeploymentsPageProps) {
  const { data, loading, error } = useApi<DeploymentsResponse>(getDeployments, [], refreshKey);

  if (loading && !data) {
    return (
      <Card>
        <LoadingState label="Loading deployments" />
      </Card>
    );
  }
  if (error && !data) {
    return (
      <Card>
        <ErrorState message={error} />
      </Card>
    );
  }
  if (!data) {
    return (
      <Card>
        <EmptyState
          title="No deployment data"
          message="The deployments endpoint returned nothing."
          tone="nodata"
        />
      </Card>
    );
  }

  const envs = [...data.environments].sort((a, b) => (a.env === "prod" ? -1 : b.env === "prod" ? 1 : 0));

  return (
    <>
      <style>{PAGE_STYLES}</style>

      <PackagesSection data={data} />

      <SummaryStrip data={data} />

      <div
        style={{
          display: "grid",
          gridTemplateColumns: `repeat(${Math.max(envs.length, 1)}, minmax(0, 1fr))`,
          gap: 16,
        }}
      >
        {envs.map((env) => (
          <EnvCard key={env.env} env={env} driftAheadOfProd={data.driftAheadOfProd} />
        ))}
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(0, 1.85fr) minmax(0, 1fr)",
          gap: 16,
          alignItems: "start",
        }}
      >
        <WorkflowRunsCard data={data} />
        <CommitsCard data={data} />
      </div>

      <SectionErrors errors={data.errors} />
    </>
  );
}
